/**
 * @fileoverview Production-ready CustomDatePicker component
 * 
 * A fully accessible, theme-aware date picker component built on Material-UI
 * with comprehensive error handling, validation, and performance optimizations.
 * 
 * Features:
 * - Full accessibility compliance (ARIA labels, keyboard navigation)
 * - Theme integration with white-label support
 * - Input validation and error handling with custom constraints
 * - Performance optimizations with React.memo and memoized callbacks
 * - Cross-browser compatibility
 * - Consistent styling with form components
 * - Enhanced UX with clear/today buttons and smart positioning
 * - Production-ready error states and user feedback
 * 
 * Usage:
 * ```tsx
 * <CustomDatePicker
 *   value={dateValue}
 *   onChange={handleDateChange}
 *   required
 *   minDate={new Date()}
 *   error={validationError}
 *   placeholder="Select a date..."
 * />
 * ```
 * 
 * @version 2.0.0
 * @since 2025-07-11
 */

import React, { memo, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { THEME, getThemeValue, getWhiteLabelThemeValue } from '@trinity/chat-components/components/Chat.Forms/DynamicForm.styles.index';
import type { Theme } from '../../types/theme';

/**
 * Type definitions for component props
 */
type StyledComponentProps = {
  theme?: Theme.Theme;
};

/**
 * Props interface for CustomDatePicker component
 */
export interface CustomDatePickerProps {
  /** Current date value in YYYY-MM-DD format */
  value: string;
  /** Callback fired when date changes */
  onChange: (date: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** HTML id attribute */
  id?: string;
  /** HTML name attribute */
  name?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether the component is in read-only mode */
  readOnly?: boolean;
  /** Error message to display */
  error?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
  /** ARIA described by for accessibility */
  'aria-describedby'?: string;
  /** Minimum allowed date */
  minDate?: Date;
  /** Maximum allowed date */
  maxDate?: Date;
  /** Custom date format */
  format?: string;
  /** Custom validation function */
  validate?: (date: Date | null) => string | null;
  /** Whether to show clear button */
  clearable?: boolean;
  /** Whether to show today button */
  showTodayButton?: boolean;
  /** Custom error messages */
  errorMessages?: {
    invalidDate?: string;
    minDate?: string;
    maxDate?: string;
    required?: string;
  };
}

/**
 * Styled wrapper component with comprehensive theming and accessibility
 */
const StyledDatePickerWrapper = styled.div<StyledComponentProps & { hasError?: boolean; isDisabled?: boolean }>(({ theme, hasError, isDisabled }) => ({
  width: '100%',
  position: 'relative',

  '& .MuiInputBase-root': {
    height: '38px',
    fontSize: THEME.fontSize.sm,
    fontFamily: getThemeValue(
      (t) => t.fonts?.regular,
      'inherit',
      theme,
    ),
    backgroundColor: isDisabled
      ? getThemeValue(
        (t) => t.palette?.gray?.light3,
        THEME.colors.backgroundLight,
        theme,
      )
      : getThemeValue(
        (t) => t.palette?.white?.main,
        THEME.colors.background,
        theme,
      ),
    border: `1px solid ${hasError
      ? getThemeValue(
        (t) => t.palette?.red?.main,
        THEME.colors.error,
        theme,
      )
      : getThemeValue(
        (t) => t.palette?.gray?.light4,
        THEME.colors.border,
        theme,
      )}`,
    borderRadius: THEME.borderRadius.md,
    padding: 0,
    cursor: isDisabled ? 'not-allowed' : 'text',
    transition: 'border-color 0.2s ease-in-out',

    '&:hover:not(.Mui-disabled)': {
      borderColor: hasError
        ? getThemeValue(
          (t) => t.palette?.red?.main,
          THEME.colors.error,
          theme,
        )
        : getThemeValue(
          (t) => t.palette?.gray?.main,
          THEME.colors.textSecondary,
          theme,
        ),
    },

    '&.Mui-focused': {
      borderColor: hasError
        ? getThemeValue(
          (t) => t.palette?.red?.main,
          THEME.colors.error,
          theme,
        )
        : getWhiteLabelThemeValue(
          (wl) => wl.primary?.main,
          (t) => t.palette?.primary?.main,
          THEME.colors.primary,
          theme,
        ),
      borderWidth: '2px',
      boxShadow: 'none',
      outline: 'none',
    },

    '&:before, &:after': {
      display: 'none',
    },

    '&.Mui-disabled': {
      backgroundColor: getThemeValue(
        (t) => t.palette?.gray?.light3,
        THEME.colors.backgroundLight,
        theme,
      ),
      borderColor: getThemeValue(
        (t) => t.palette?.gray?.light4,
        THEME.colors.border,
        theme,
      ),
      cursor: 'not-allowed',
    },
  },

  '& .MuiInputBase-input': {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    color: isDisabled
      ? getThemeValue(
        (t) => t.palette?.gray?.main,
        THEME.colors.textSecondary,
        theme,
      )
      : getThemeValue(
        (t) => t.palette?.text?.primary,
        THEME.colors.text,
        theme,
      ),
    fontSize: THEME.fontSize.sm,
    fontFamily: getThemeValue(
      (t) => t.fonts?.regular,
      'inherit',
      theme,
    ),
    height: 'auto',
    cursor: isDisabled ? 'not-allowed' : 'text',

    '&::placeholder': {
      color: getThemeValue(
        (t) => t.palette?.gray?.main,
        THEME.colors.textSecondary,
        theme,
      ),
      opacity: 1,
    },

    '&:disabled': {
      cursor: 'not-allowed',
      color: getThemeValue(
        (t) => t.palette?.gray?.main,
        THEME.colors.textSecondary,
        theme,
      ),
    },
  },

  '& .MuiInputAdornment-root': {
    marginRight: THEME.spacing.sm,
  },

  '& .MuiSvgIcon-root': {
    color: isDisabled
      ? getThemeValue(
        (t) => t.palette?.gray?.light4,
        THEME.colors.border,
        theme,
      )
      : getThemeValue(
        (t) => t.palette?.gray?.main,
        THEME.colors.textSecondary,
        theme,
      ),
    fontSize: '20px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'color 0.2s ease-in-out',

    '&:hover': {
      color: !isDisabled ? getThemeValue(
        (t) => t.palette?.text?.primary,
        THEME.colors.text,
        theme,
      ) : undefined,
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    display: 'none',
  },

  // Enhanced calendar popup styling
  '& .MuiPaper-root': {
    borderRadius: THEME.borderRadius.md,
    boxShadow: THEME.boxShadow.lg,
    border: `1px solid ${getThemeValue(
      (t) => t.palette?.gray?.light4,
      THEME.colors.border,
      theme,
    )}`,
    marginTop: THEME.spacing.xs,
  },

  '& .MuiPickersDay-root': {
    fontSize: THEME.fontSize.sm,
    fontFamily: getThemeValue(
      (t) => t.fonts?.regular,
      'inherit',
      theme,
    ),
    borderRadius: THEME.borderRadius.sm,
    transition: 'all 0.2s ease-in-out',

    '&.Mui-selected': {
      backgroundColor: getWhiteLabelThemeValue(
        (wl) => wl.primary?.main,
        (t) => t.palette?.primary?.main,
        THEME.colors.primary,
        theme,
      ),
      color: getThemeValue(
        (t) => t.palette?.white?.main,
        THEME.colors.background,
        theme,
      ),
      fontWeight: 600,

      '&:hover': {
        backgroundColor: getWhiteLabelThemeValue(
          (wl) => wl.primary?.dark,
          (t) => t.palette?.primary?.dark1,
          THEME.colors.primaryHover,
          theme,
        ),
      },

      '&:focus': {
        backgroundColor: getWhiteLabelThemeValue(
          (wl) => wl.primary?.main,
          (t) => t.palette?.primary?.main,
          THEME.colors.primary,
          theme,
        ),
      },
    },

    '&:hover:not(.Mui-selected)': {
      backgroundColor: getThemeValue(
        (t) => t.palette?.gray?.light3,
        THEME.colors.backgroundLight,
        theme,
      ),
    },

    '&:focus': {
      outline: `2px solid ${getWhiteLabelThemeValue(
        (wl) => wl.primary?.main,
        (t) => t.palette?.primary?.main,
        THEME.colors.primary,
        theme,
      )}`,
      outlineOffset: '2px',
    },

    '&.Mui-disabled': {
      color: getThemeValue(
        (t) => t.palette?.gray?.light4,
        THEME.colors.border,
        theme,
      ),
    },
  },

  '& .MuiPickersCalendarHeader-root': {
    paddingLeft: THEME.spacing.lg,
    paddingRight: THEME.spacing.lg,
    marginBottom: THEME.spacing.sm,
  },

  '& .MuiPickersCalendarHeader-label': {
    fontSize: THEME.fontSize.sm,
    fontFamily: getThemeValue(
      (t) => t.fonts?.bold,
      'inherit',
      theme,
    ),
    fontWeight: 700,
    color: getThemeValue(
      (t) => t.palette?.text?.primary,
      THEME.colors.text,
      theme,
    ),
  },

  '& .MuiPickersArrowSwitcher-root': {
    '& .MuiIconButton-root': {
      padding: THEME.spacing.xs,

      '&:hover': {
        backgroundColor: getThemeValue(
          (t) => t.palette?.gray?.light3,
          THEME.colors.backgroundLight,
          theme,
        ),
      },

      '&:focus': {
        outline: `2px solid ${getWhiteLabelThemeValue(
          (wl) => wl.primary?.main,
          (t) => t.palette?.primary?.main,
          THEME.colors.primary,
          theme,
        )}`,
        outlineOffset: '2px',
      },
    },
  },

  '& .MuiDayCalendar-weekDayLabel': {
    fontSize: THEME.fontSize.xs,
    fontWeight: 600,
    color: getThemeValue(
      (t) => t.palette?.gray?.main,
      THEME.colors.textSecondary,
      theme,
    ),
  },
}));

/**
 * Error message component
 */
const ErrorMessage = styled.div<StyledComponentProps>(({ theme }) => ({
  color: getThemeValue(
    (t) => t.palette?.red?.main,
    THEME.colors.error,
    theme,
  ),
  fontSize: THEME.fontSize.xs,
  marginTop: THEME.spacing.xs,
  display: 'flex',
  alignItems: 'center',
  gap: THEME.spacing.xs,
}));

/**
 * Utility functions for date validation and formatting
 */
const validateDate = (date: Date | null): boolean => {
  if (!date) return true; // null/empty is valid
  return date instanceof Date && !isNaN(date.getTime());
};

const formatDateForInput = (date: Date): string => {
  try {
    // Use local timezone instead of UTC to avoid "off by 1" issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

const parseDateFromString = (dateString: string): Date | null => {
  if (!dateString) return null;
  try {
    // Parse as local date to avoid timezone conversion issues
    const [year, month, day] = dateString.split('-').map(Number);
    if (year && month && day) {
      const date = new Date(year, month - 1, day);
      return validateDate(date) ? date : null;
    }
    return null;
  } catch (error) {
    console.error('Date parsing error:', error);
    return null;
  }
};

/**
 * Enhanced date validation with custom constraints
 */
const validateDateConstraints = (
  date: Date | null,
  minDate?: Date,
  maxDate?: Date,
  required = false,
  customValidator?: (date: Date | null) => string | null,
): string | null => {
  // Check if required and empty
  if (required && !date) {
    return 'Date is required';
  }

  // If no date and not required, it's valid
  if (!date) return null;

  // Check if valid date
  if (!validateDate(date)) {
    return 'Please enter a valid date';
  }

  // Check min date constraint
  if (minDate && date < minDate) {
    return `Date must be on or after ${formatDateForInput(minDate)}`;
  }

  // Check max date constraint
  if (maxDate && date > maxDate) {
    return `Date must be on or before ${formatDateForInput(maxDate)}`;
  }

  // Run custom validation
  if (customValidator) {
    const customError = customValidator(date);
    if (customError) return customError;
  }

  return null;
};

/**
 * Production-ready CustomDatePicker Component
 * 
 * A comprehensive date picker with full accessibility, error handling,
 * and theme integration for professional applications.
 */
export const CustomDatePicker: React.FC<CustomDatePickerProps> = memo(({
  value,
  onChange,
  placeholder = 'Select date...',
  required = false,
  disabled = false,
  readOnly = false,
  error,
  id,
  name,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  minDate,
  maxDate,
  format = 'MM/dd/yyyy',
  validate,
  clearable = true,
  showTodayButton = true,
  errorMessages = {},
}) => {
  // Memoize date value to prevent unnecessary re-renders
  const dateValue = useMemo(() => parseDateFromString(value), [value]);

  // Enhanced validation with constraints
  const validationError = useMemo(() => {
    if (error) return error; // Use explicit error if provided
    return validateDateConstraints(dateValue, minDate, maxDate, required, validate);
  }, [error, dateValue, minDate, maxDate, required, validate]);

  // Memoize error state
  const hasError = useMemo(() => Boolean(validationError), [validationError]);

  // Memoize accessibility props
  const accessibilityProps = useMemo(() => {
    const props: Record<string, string | boolean> = {
      'aria-label': ariaLabel || `Date picker${required ? ' (required)' : ''}`,
      'aria-invalid': hasError,
      'aria-required': required,
    };

    if (ariaDescribedBy) {
      props['aria-describedby'] = ariaDescribedBy;
    }

    if (hasError && validationError) {
      const errorId = `${id || 'datepicker'}-error`;
      props['aria-describedby'] = ariaDescribedBy
        ? `${ariaDescribedBy} ${errorId}`
        : errorId;
    }

    return props;
  }, [ariaLabel, required, hasError, validationError, id, ariaDescribedBy]);

  // Optimized date change handler with enhanced validation
  const handleDateChange = useCallback((date: Date | null) => {
    try {
      if (date && validateDate(date)) {
        const formattedDate = formatDateForInput(date);
        onChange(formattedDate);
      } else {
        onChange('');
      }
    } catch (error) {
      console.error('Date change error:', error);
      onChange('');
    }
  }, [onChange]);

  // Handle clear action
  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  // Handle today action
  const handleToday = useCallback(() => {
    const today = new Date();
    const formattedDate = formatDateForInput(today);
    onChange(formattedDate);
  }, [onChange]);

  // Error message ID for accessibility
  const errorId = useMemo(() =>
    hasError && validationError ? `${id || 'datepicker'}-error` : undefined,
    [hasError, validationError, id]);

  // Action bar configuration
  const actionBarActions = useMemo(() => {
    const actions: ('clear' | 'today')[] = [];
    if (clearable) actions.push('clear');
    if (showTodayButton) actions.push('today');
    return actions;
  }, [clearable, showTodayButton]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledDatePickerWrapper
        hasError={hasError}
        isDisabled={disabled}
        role="group"
        aria-labelledby={id ? `${id}-label` : undefined}
      >
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          readOnly={readOnly}
          minDate={minDate}
          maxDate={maxDate}
          format={format}
          slotProps={{
            textField: {
              placeholder,
              required,
              id,
              name,
              variant: 'outlined',
              fullWidth: true,
              error: hasError,
              inputProps: {
                ...accessibilityProps,
                'data-testid': id ? `${id}-input` : 'date-picker-input',
              },
            },
            popper: {
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: false,
                    rootBoundary: 'viewport',
                  },
                },
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
                  },
                },
              ],
            },
            openPickerIcon: {
              'data-testid': id ? `${id}-open-button` : 'date-picker-open-button',
            },
            actionBar: {
              actions: actionBarActions,
            },
          }}
        />

        {/* Error message with proper accessibility */}
        {hasError && validationError && (
          <ErrorMessage
            id={errorId}
            role="alert"
            aria-live="polite"
            data-testid={id ? `${id}-error` : 'date-picker-error'}
          >
            <span aria-hidden="true">⚠</span>
            {validationError}
          </ErrorMessage>
        )}
      </StyledDatePickerWrapper>
    </LocalizationProvider>
  );
});

// Display name for debugging
CustomDatePicker.displayName = 'CustomDatePicker';
